"use client";

import { Dialog } from "@ark-ui/react/dialog";
import { Button } from "@decibeltrade/web-core/components";
import { ModalContent, ModalRenderProps, openModal } from "@decibeltrade/web-core/modal";
import { useTranslations } from "next-intl";
import { useEffect } from "react";
import { css } from "styled-system/css";
import { flex, stack } from "styled-system/patterns";

import { openWalletSelectorModal } from "@/components/modal/wallet-selector-modal";
import { useSuiAccountMismatch } from "@/hooks/use-sui-account-mismatch";

import { useWriteDexSdk } from "./write-dex-sdk-context";

export function SuiAccountMismatchModal() {
  const hasMismatch = useSuiAccountMismatch();

  useEffect(() => {
    if (!hasMismatch) return;
    openModal({
      id: "sui-account-mismatch-modal",
      renderContent: (modalProps) => <SuiAccountMismatchModalContent {...modalProps} />,
      dismissible: false,
    });
  }, [hasMismatch]);

  return null;
}

export function SuiAccountMismatchModalContent({ close }: ModalRenderProps) {
  const t = useTranslations("walletSelector");

  const hasMismatch = useSuiAccountMismatch();
  const { disconnect } = useWriteDexSdk();

  // Switching back to the session account in the wallet clears the mismatch, so the modal
  // dismisses itself without the user having to act on it.
  useEffect(() => {
    if (!hasMismatch) {
      close();
    }
  }, [hasMismatch, close]);

  const onDisconnect = () => {
    disconnect();
    close();
  };

  const onReconnect = () => {
    disconnect();
    close();
    openWalletSelectorModal();
  };

  return (
    <ModalContent className={stack({ w: "448", p: "16", gap: "24" })}>
      <div className={stack({ gap: "12", pt: "12", textAlign: "center" })}>
        {/* `Dialog.Content` labels itself by the title's id, and this modal has no close button
            or backdrop dismiss — without it a screen reader announces an unnamed, inescapable
            dialog. */}
        <Dialog.Title asChild>
          <h2 className={css({ textStyle: "heading.3xs" })}>{t("suiAccountMismatch")}</h2>
        </Dialog.Title>
        <p className={css({ textStyle: "body.md", color: "text.tertiary" })}>
          {t("suiAccountMismatchDescription")}
        </p>
      </div>
      <div className={flex({ direction: "column", gap: "8" })}>
        <Button variant="secondary" fullWidth onClick={onDisconnect}>
          {t("disconnect")}
        </Button>
        <Button variant="primary" fullWidth onClick={onReconnect}>
          {t("reconnectWallet")}
        </Button>
      </div>
    </ModalContent>
  );
}
